import { useRecentlyChangedRowsApi } from "@/contexts/RecentlyChangedRowsContext";
import { useDealStages } from "@/contexts/DealStagesContext";
import { useOffices } from "@/hooks/useOffices";
import type { ProjectSummary } from "@/hooks/useProjectBoardData";
import { db } from "@/lib/supabase";
import { fullRowChanges, recordAudit } from "@/lib/audit";
import type { Database } from "@/types/database.types";
import {
  computeNumWeeks,
  computeProjectEndDate,
  currentWeekStartISO,
} from "@/utils/dateUtils";
import { useState } from "react";
import { MonDateInput } from "../WeekStepper";
import { ClientCombobox } from "../clients/ClientCombobox";

interface ProjectEditorProps {
  /** Deal-stage id new projects start at (the category's default deal stage). */
  defaultDealStageId: string;
  /** Category id the new project is created in. Falls back to the DB default when omitted. */
  defaultCategory?: string;
  onSave: () => void;
  onCancel: () => void;
  onOptimisticCreate?: (project: ProjectSummary) => void;
  onOptimisticConfirm?: (
    projectId: string,
    project: Database["public"]["Tables"]["projects"]["Row"]
  ) => void;
  onOptimisticError?: (projectId: string) => void;
}

export function ProjectEditor({
  defaultDealStageId,
  defaultCategory,
  onSave,
  onCancel,
  onOptimisticCreate,
  onOptimisticConfirm,
  onOptimisticError,
}: ProjectEditorProps) {
  const { offices } = useOffices();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { markSelfEdit } = useRecentlyChangedRowsApi();
  const { byId } = useDealStages();
  const optimisticCallbacksAvailable = Boolean(
    defaultCategory &&
    onOptimisticCreate &&
    onOptimisticConfirm &&
    onOptimisticError
  );

  const [formData, setFormData] = useState<{
    name: string;
    client: string;
    client_id: string | null;
    office_id: string;
    start_date: string;
    num_weeks: number;
  }>({
    name: "",
    client: "",
    client_id: null,
    office_id: offices.find((o) => o.name === "Ann Arbor")?.id ?? "",
    // Default to the same local week the picker's floor uses.
    start_date: currentWeekStartISO(),
    num_weeks: 8,
  });

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError("Project name is required");
      return false;
    }


    if (!formData.office_id) {
      setError("Office is required");
      return false;
    }

    const selectedOffice = offices.find((o) => o.id === formData.office_id);
    if (!selectedOffice || !selectedOffice.is_active) {
      setError("Cannot assign project to inactive office");
      return false;
    }

    if (!formData.start_date) {
      setError("Start date is required");
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    setError(null);

    if (!validateForm()) {
      return;
    }

    const stage = byId.get(defaultDealStageId);
    if (!stage) {
      setError("Invalid deal stage");
      return;
    }

    const selectedOffice = offices.find((office) => office.id === formData.office_id)!;
    const projectId = crypto.randomUUID();
    const now = new Date().toISOString();
    const canInsertOptimistically = optimisticCallbacksAvailable;

    setLoading(true);

    try {
      const projectData: Database["public"]["Tables"]["projects"]["Insert"] = {
        id: projectId,
        name: formData.name.trim(),
        office_id: formData.office_id,
        client: formData.client.trim() || null,
        client_id: formData.client_id,
        start_date: formData.start_date,
        num_weeks: formData.num_weeks,
        deal_stage_id: stage.id,
        // Seed likelihood from the default stage's configured value.
        likelihood_to_close: stage.likelihood_to_close,
        // Omit when unknown so the column's DB default (Potential) applies.
        ...(defaultCategory ? { category_id: defaultCategory } : {}),
      };

      if (canInsertOptimistically) {
        onOptimisticCreate?.({
          id: projectId,
          name: projectData.name,
          color: null,
          budget: null,
          category_id: defaultCategory!,
          deal_stage_id: stage.id,
          deal_stage: stage,
          likelihood_to_close: stage.likelihood_to_close,
          start_date: formData.start_date,
          end_date: computeProjectEndDate(formData.start_date, formData.num_weeks),
          num_weeks: formData.num_weeks,
          office_id: formData.office_id,
          office: {
            name: selectedOffice.name,
            is_active: selectedOffice.is_active,
          },
          client: formData.client.trim() || null,
          client_id: formData.client_id,
          description: null,
          potential_future_work: null,
          tech_stack: null,
          engagement_type: null,
          team_success_ingredients: null,
          created_at: now,
          updated_at: now,
          project_assignments: [],
          optimisticStatus: "saving",
        });
      }

      // Keep the realtime echo from replacing the optimistic row with a refetch.
      markSelfEdit("projects", projectId, 10_000);
      const { data: createdProject, error: createError } = await db.projects.create(projectData);

      if (createError) {
        if (canInsertOptimistically) onOptimisticError?.(projectId);
        setError(createError.message ?? "Failed to create project");
        return;
      }

      if (!createdProject) {
        if (canInsertOptimistically) onOptimisticError?.(projectId);
        setError("Failed to create project");
        return;
      }

      if (canInsertOptimistically) {
        onOptimisticConfirm?.(projectId, createdProject);
      }

      recordAudit({
        table: "projects",
        recordId: createdProject.id,
        operation: "INSERT",
        changes: fullRowChanges(createdProject, "INSERT"),
        recordLabel: createdProject.name,
        officeId: createdProject.office_id ?? null,
      });

      onSave();
    } catch (err) {
      if (canInsertOptimistically) onOptimisticError?.(projectId);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to create project"
      );
    } finally {
      setLoading(false);
    }
  };

  // Keep this component mounted while the request is pending so a failed save
  // can restore every field exactly as the user entered it. The optimistic row
  // occupies the board while this existing editor is temporarily hidden.
  if (loading && optimisticCallbacksAvailable) return null;

  return (
    <div className="assignment-editor-wrapper">
      <div className="assignment-editor" data-testid="project-editor">
        {error && (
          <div className="assignment-editor__error" role="alert">
            <span>{error}</span>
          </div>
        )}

        <div className="project-editor__fields">
          <div className="assignment-editor__field">
            <label className="assignment-editor__label">Project Name *</label>
            <input
              type="text"
              className="assignment-editor__input"
              data-testid="project-name-input"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && !loading) {
                  handleSave();
                }
              }}
              autoFocus
              disabled={loading}
            />
          </div>

          <div className="assignment-editor__field">
            <label className="assignment-editor__label">Client Name</label>
            <ClientCombobox
              value={{ id: formData.client_id, name: formData.client }}
              onChange={(sel) =>
                setFormData({ ...formData, client_id: sel.id, client: sel.name })
              }
              disabled={loading}
              placeholder="Select or type a client..."
            />
          </div>

          <div className="assignment-editor__field">
            <label className="assignment-editor__label">Office *</label>
            <select
              className="assignment-editor__select"
              data-testid="project-office-select"
              value={formData.office_id}
              onChange={(e) =>
                setFormData({ ...formData, office_id: e.target.value })
              }
              disabled={loading}
            >
              <option value="">Select an office</option>
              {offices
                .filter((o) => o.is_active)
                .map((office) => (
                  <option key={office.id} value={office.id}>
                    {office.name}
                  </option>
                ))}
            </select>
          </div>

          <div className="assignment-editor__field">
            <label className="assignment-editor__label">Start Date *</label>
            <MonDateInput
              className="assignment-editor__input"
              testId="project-start-date-input"
              value={formData.start_date}
              minDate={currentWeekStartISO()}
              onChange={(value) =>
                setFormData({ ...formData, start_date: value })
              }
              autoFocus={false}
              disabled={loading}
            />
          </div>

          <div className="assignment-editor__field">
            <label className="assignment-editor__label">End Date *</label>
            <MonDateInput
              className="assignment-editor__input"
              testId="project-end-date-input"
              value={computeProjectEndDate(formData.start_date, formData.num_weeks)}
              minDate={computeProjectEndDate(formData.start_date, 2)}
              onChange={(value) =>
                setFormData({ ...formData, num_weeks: computeNumWeeks(formData.start_date, value) })
              }
              autoFocus={false}
              disabled={loading}
            />
          </div>
        </div>

        <div className="assignment-editor__actions">
          <button
            type="button"
            className="ghost-button"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="primary-button"
            data-testid="create-project-button"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? "Creating..." : "Create Project"}
          </button>
        </div>
      </div>
    </div>
  );
}
